import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, RotateCcw, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';

const MIN_SCALE = 0.5;
const MAX_SCALE = 8;
const SWIPE_DISMISS_THRESHOLD = 80; // px vertical
const SWIPE_NAV_THRESHOLD = 60;     // px horizontal

// Pick the highest-bitrate MP4 from a video_src array
const getBestMp4 = (videoSrc) => {
  if (!videoSrc || videoSrc.length === 0) return null;
  const mp4s = videoSrc.filter(v => v.content_type === 'video/mp4');
  if (mp4s.length === 0) return null;
  return mp4s.reduce((best, v) => (v.bitrate > best.bitrate ? v : best)).url;
};

// mediaList: array of { src, alt, type, videoSrc?, tweetUrl? }
const ImagePreviewModal = ({ mediaList, initialIndex = 0, onClose }) => {
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  // Vertical swipe-to-dismiss state
  const [swipeDelta, setSwipeDelta] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  // Horizontal swipe-to-navigate state
  const [hSwipeDelta, setHSwipeDelta] = useState(0);
  const [isHSwiping, setIsHSwiping] = useState(false);

  const dragStart = useRef(null);
  const didDrag = useRef(false);
  const swipeTouchStart = useRef(null);
  const lastPinchDistance = useRef(null);
  const contentAreaRef = useRef(null);
  const contextMenuOpen = useRef(false);

  const current = mediaList[activeIndex];
  const isVideo = current.type === 'video';
  const canNavigate = mediaList.length > 1;

  const resetZoom = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  const goNext = useCallback(() => {
    setActiveIndex(i => (i + 1) % mediaList.length);
    resetZoom();
  }, [mediaList.length]);

  const goPrev = useCallback(() => {
    setActiveIndex(i => (i - 1 + mediaList.length) % mediaList.length);
    resetZoom();
  }, [mediaList.length]);

  // Close on Escape, navigate on arrow keys
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        // If the browser context menu was open, Escape closes it — don't also close the modal
        if (contextMenuOpen.current) { contextMenuOpen.current = false; return; }
        onClose();
      }
      if (e.key === 'ArrowRight' && canNavigate) goNext();
      if (e.key === 'ArrowLeft' && canNavigate) goPrev();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, goNext, goPrev, canNavigate]);

  // Lock body scroll and preserve scroll position on restore
  useEffect(() => {
    const scrollY = window.scrollY;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
      window.scrollTo(0, scrollY);
    };
  }, []);

  // ── Zoom / pan (image only) ──────────────────────────────────────────────

  // Attach wheel as non-passive so preventDefault() works
  useEffect(() => {
    const el = contentAreaRef.current;
    if (!el || isVideo) return;
    const onWheel = (e) => {
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.1 : 0.9;
      setScale(prev => {
        const next = Math.max(MIN_SCALE, Math.min(MAX_SCALE, prev * factor));
        if (next <= 1) setPosition({ x: 0, y: 0 });
        return next;
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [isVideo]);

  const handleMouseDown = (e) => {
    if (isVideo || scale <= 1) return;
    e.preventDefault();
    setIsDragging(true);
    dragStart.current = { x: e.clientX - position.x, y: e.clientY - position.y };
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !dragStart.current) return;
    didDrag.current = true;
    setPosition({
      x: e.clientX - dragStart.current.x,
      y: e.clientY - dragStart.current.y,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    dragStart.current = null;
  };

  // ── Touch: pinch-zoom, pan, OR swipe-to-dismiss / swipe-to-navigate ─────

  const handleTouchStart = (e) => {
    if (isVideo) {
      if (e.touches.length === 1) {
        swipeTouchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
      return;
    }

    if (e.touches.length === 2) {
      swipeTouchStart.current = null;
      lastPinchDistance.current = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
    } else if (e.touches.length === 1) {
      swipeTouchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      if (scale > 1) {
        dragStart.current = {
          x: e.touches[0].clientX - position.x,
          y: e.touches[0].clientY - position.y,
        };
      }
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2 && !isVideo) {
      e.preventDefault();
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      if (lastPinchDistance.current) {
        const delta = dist / lastPinchDistance.current;
        setScale(prev => {
          const next = Math.max(MIN_SCALE, Math.min(MAX_SCALE, prev * delta));
          if (next <= 1) setPosition({ x: 0, y: 0 });
          return next;
        });
      }
      lastPinchDistance.current = dist;
      return;
    }

    if (e.touches.length === 1) {
      const dy = e.touches[0].clientY - (swipeTouchStart.current?.y ?? e.touches[0].clientY);
      const dx = e.touches[0].clientX - (swipeTouchStart.current?.x ?? e.touches[0].clientX);

      // Swipe-to-dismiss: single finger, not zoomed, primarily vertical downward
      if (scale <= 1 && dy > 0 && Math.abs(dy) > Math.abs(dx) * 1.5) {
        e.preventDefault();
        setIsSwiping(true);
        setSwipeDelta(dy);
        return;
      }

      // Swipe-to-navigate: single finger, not zoomed, primarily horizontal, multiple images
      if (scale <= 1 && canNavigate && Math.abs(dx) > Math.abs(dy) * 1.5) {
        e.preventDefault();
        setIsHSwiping(true);
        setHSwipeDelta(dx);
        return;
      }

      // Pan while zoomed in
      if (!isVideo && scale > 1 && dragStart.current) {
        e.preventDefault();
        setPosition({
          x: e.touches[0].clientX - dragStart.current.x,
          y: e.touches[0].clientY - dragStart.current.y,
        });
      }
    }
  };

  const handleTouchEnd = () => {
    lastPinchDistance.current = null;
    dragStart.current = null;

    if (isSwiping) {
      if (swipeDelta >= SWIPE_DISMISS_THRESHOLD) {
        onClose();
      } else {
        setSwipeDelta(0);
      }
      setIsSwiping(false);
    }

    if (isHSwiping) {
      if (hSwipeDelta < -SWIPE_NAV_THRESHOLD) {
        goNext();
      } else if (hSwipeDelta > SWIPE_NAV_THRESHOLD) {
        goPrev();
      } else {
        setHSwipeDelta(0);
      }
      setIsHSwiping(false);
    }

    swipeTouchStart.current = null;
  };

  const handleReset = (e) => {
    e.stopPropagation();
    resetZoom();
  };

  const imageCursor = scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in';

  // Opacity dims as user swipes down
  const swipeOpacity = isSwiping ? Math.max(0.3, 1 - swipeDelta / 300) : 1;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/90 flex flex-col"
      style={{ opacity: swipeOpacity, transition: isSwiping ? 'none' : 'opacity 0.2s ease' }}
      onClick={e => { e.stopPropagation(); onClose(); }}
    >
      {/* Fixed header bar — always visible, never obscured by tall images */}
      <div
        className="flex-shrink-0 flex items-center justify-between px-4 py-3"
        style={{ transform: `translateY(${swipeDelta}px)`, transition: isSwiping ? 'none' : undefined }}
        onClick={e => e.stopPropagation()}
      >
        {/* Drag handle centered */}
        <div className="w-10 h-1 rounded-full bg-white/40 mx-auto absolute left-1/2 -translate-x-1/2 pointer-events-none top-4" />

        {/* Spacer so buttons sit on the right */}
        <div className="flex-1" />

        <div className="flex gap-2">
          {!isVideo && scale !== 1 && (
            <button
              onClick={handleReset}
              className="p-3 bg-white/10 hover:bg-white/20 active:bg-white/30 rounded-full text-white transition-colors touch-manipulation"
              title="Reset zoom"
            >
              <RotateCcw size={20} />
            </button>
          )}
          <button
            onClick={e => { e.stopPropagation(); onClose(); }}
            className="p-3 bg-white/10 hover:bg-white/20 active:bg-white/30 rounded-full text-white transition-colors touch-manipulation"
            title="Close (Esc)"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Zoom level badge */}
      {!isVideo && scale !== 1 && (
        <div className="absolute bottom-14 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/50 rounded-full text-white text-sm z-10 pointer-events-none">
          {Math.round(scale * 100)}%
        </div>
      )}

      {/* Left / right nav arrows — all screen sizes, when multiple items */}
      {canNavigate && (
        <>
          <button
            onClick={e => { e.stopPropagation(); goPrev(); }}
            className="flex absolute left-3 top-1/2 -translate-y-1/2 z-10 p-3 bg-white/10 hover:bg-white/25 active:bg-white/35 rounded-full text-white transition-colors touch-manipulation"
            title="Previous (←)"
          >
            <ChevronLeft size={28} />
          </button>
          <button
            onClick={e => { e.stopPropagation(); goNext(); }}
            className="flex absolute right-3 top-1/2 -translate-y-1/2 z-10 p-3 bg-white/10 hover:bg-white/25 active:bg-white/35 rounded-full text-white transition-colors touch-manipulation"
            title="Next (→)"
          >
            <ChevronRight size={28} />
          </button>
        </>
      )}

      {/* Content area — fills remaining space below the header */}
      <div
        ref={contentAreaRef}
        className="flex-1 flex items-center justify-center overflow-hidden"
        onClick={e => {
          e.stopPropagation();
          if (didDrag.current) { didDrag.current = false; return; }
          onClose();
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          cursor: isVideo ? 'default' : imageCursor,
          transform: `translate(${hSwipeDelta}px, ${swipeDelta}px)`,
          transition: (isSwiping || isHSwiping) ? 'none' : 'transform 0.25s ease',
        }}
      >
        {isVideo ? (
          <div className="flex flex-col items-center gap-4 max-w-[95vw]" onClick={e => e.stopPropagation()}>
            <img
              src={current.src}
              alt={current.alt}
              className="max-h-[75vh] rounded-lg object-contain"
              draggable={false}
            />
            {current.tweetUrl && (
              <a
                href={current.tweetUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 active:bg-white/30 rounded-full text-white text-sm transition-colors touch-manipulation"
                onClick={e => e.stopPropagation()}
              >
                <ExternalLink size={16} />
                Open video on X
              </a>
            )}
          </div>
        ) : (
          <img
            src={current.src}
            alt={current.alt}
            draggable={false}
            onClick={e => e.stopPropagation()}
            onContextMenu={e => { e.stopPropagation(); contextMenuOpen.current = true; }}
            style={{
              transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
              transition: isDragging ? 'none' : 'transform 0.15s ease-out',
              maxWidth: '95vw',
              maxHeight: '100%',
              objectFit: 'contain',
              userSelect: 'none',
              WebkitUserSelect: 'none',
              borderRadius: '8px',
            }}
          />
        )}
      </div>

      {/* Bottom bar: dots indicator + swipe hint */}
      <div
        className="flex-shrink-0 flex flex-col items-center gap-1 py-3 pointer-events-none select-none"
        style={{ transform: `translateY(${swipeDelta}px)`, transition: isSwiping ? 'none' : undefined }}
      >
        {canNavigate && (
          <div className="flex gap-1.5">
            {mediaList.map((_, i) => (
              <div
                key={i}
                className={`rounded-full transition-all ${i === activeIndex ? 'w-4 h-1.5 bg-white' : 'w-1.5 h-1.5 bg-white/40'}`}
              />
            ))}
          </div>
        )}
        <div className="text-white/40 text-xs sm:hidden">
          Swipe down or tap outside to close
        </div>
      </div>
    </div>
  );
};

export default ImagePreviewModal;
